import { useState, useEffect } from 'react';
import { isDemoBrand } from '../utils/constants';
import { formatDateForAPI } from '../utils/formatters';
import { fetchWithBrand } from '../utils/api';

// Helper: format Date to YYYY-MM-DD (local time)
const formatYMD = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Combined hook for IKAS/Shopify/Woo/Adjust order summaries + product analytics.
 *
 * @param {string} brandId
 * @param {string} dateRange
 * @param {Date|null} startDate
 * @param {Date|null} endDate
 * @param {{ hasIkasConnected: boolean, hasShopifyConnected: boolean, hasWooConnected: boolean }} connections
 * @param {Function} getComparisonWindows
 */
export function useEcommerce(brandId, dateRange, startDate, endDate, connections, getComparisonWindows) {
  const { hasIkasConnected, hasShopifyConnected, hasWooConnected } = connections || {};

  const [hasAdjustConnected, setHasAdjustConnected] = useState(false);
  const [ikasSummary, setIkasSummary] = useState(null);
  const [ikasDailyRows, setIkasDailyRows] = useState([]);
  const [shopifySummary, setShopifySummary] = useState(null);
  const [shopifyDailyRows, setShopifyDailyRows] = useState([]);
  const [wooSummary, setWooSummary] = useState(null);
  const [wooDailyRows, setWooDailyRows] = useState([]);
  const [adjustSummary, setAdjustSummary] = useState(null);
  const [adjustDailyRows, setAdjustDailyRows] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [categoryPerformance, setCategoryPerformance] = useState([]);
  const [dailyProductData, setDailyProductData] = useState({ data: [], totals: {}, changes: {} });
  const [isLoading, setIsLoading] = useState({
    ikas: false, shopify: false, woo: false, adjust: false,
    products: true, categories: true, daily: true,
  });

  // --- Detect Adjust connection on mount ---
  useEffect(() => {
    if (!brandId) return;
    (async () => {
      try {
        const res = await fetchWithBrand('/api/auth/connections', brandId);
        if (!res.ok) return;
        const data = await res.json();
        let connected = false;
        if (Array.isArray(data)) {
          connected = !!data.find(c => String(c.platform || '').toUpperCase() === 'ADJUST' && String(c.status || '').toUpperCase() === 'ACTIVE');
        } else if (data && typeof data === 'object') {
          const adj = data.ADJUST || data.adjust;
          connected = !!(adj && String(adj.status || '').toUpperCase() === 'ACTIVE');
        }
        setHasAdjustConnected(connected);
      } catch {
        // silent
      }
    })();
  }, [brandId]);

  // --- IKAS Summary ---
  useEffect(() => {
    if (!hasIkasConnected) {
      setIkasSummary(null);
      return;
    }
    if (!brandId) return;
    const windows = getComparisonWindows();
    if (!windows) return;

    const fetchIkasSummary = async () => {
      try {
        setIsLoading(prev => ({ ...prev, ikas: true }));
        const params = new URLSearchParams({ startDate: windows.current.start, endDate: windows.current.end });
        const res = await fetchWithBrand(`/api/ikas/orders/daily?${params.toString()}`, brandId);
        if (!res.ok) {
          setIkasSummary(null);
          return;
        }
        const js = await res.json().catch(() => ({}));
        const rows = Array.isArray(js?.rows) ? js.rows : [];
        const totals = rows.reduce((acc, r) => {
          acc.revenue += Number(r.revenue || 0);
          acc.orders += Number(r.orders_count || 0);
          acc.items += Number(r.items_count || 0);
          return acc;
        }, { revenue: 0, orders: 0, items: 0 });
        setIkasSummary(totals);

        // Fill missing dates with zero values
        try {
          const rowMap = new Map();
          for (const r of rows) {
            const d = r.day || r.date;
            if (d) {
              const dateKey = typeof d === 'string' && d.length >= 10 ? d.slice(0, 10) : formatYMD(new Date(d));
              rowMap.set(dateKey, r);
            }
          }
          const allDays = [];
          const cur = new Date(windows.current.start + 'T00:00:00');
          const end = new Date(windows.current.end + 'T00:00:00');
          while (cur <= end) {
            const key = formatYMD(cur);
            if (rowMap.has(key)) {
              allDays.push(rowMap.get(key));
            } else {
              allDays.push({ day: key, orders_count: 0, items_count: 0, revenue: 0 });
            }
            cur.setDate(cur.getDate() + 1);
          }
          const sorted = [...allDays].sort((a, b) => new Date(b.day || b.date) - new Date(a.day || a.date));
          setIkasDailyRows(sorted.slice(0, 10));
        } catch {
          setIkasDailyRows(rows.slice(-10).reverse());
        }
      } catch {
        setIkasSummary(null);
        setIkasDailyRows([]);
      } finally {
        setIsLoading(prev => ({ ...prev, ikas: false }));
      }
    };

    fetchIkasSummary();
  }, [brandId, dateRange, startDate, endDate, hasIkasConnected, getComparisonWindows]);

  // --- Shopify Summary ---
  useEffect(() => {
    if (!hasShopifyConnected) {
      setShopifySummary(null);
      return;
    }
    if (!brandId) return;
    const windows = getComparisonWindows();
    if (!windows) return;

    const fetchShopifySummary = async () => {
      try {
        setIsLoading(prev => ({ ...prev, shopify: true }));
        const params = new URLSearchParams({ startDate: windows.current.start, endDate: windows.current.end });
        const res = await fetchWithBrand(`/api/shopify/orders/totals/daily?${params.toString()}`, brandId);
        if (!res.ok) {
          setShopifySummary(null);
          return;
        }
        const js = await res.json().catch(() => ({}));
        const rows = Array.isArray(js?.rows) ? js.rows : [];
        const totals = rows.reduce((acc, r) => {
          acc.revenue += Number(r.total_value_sum || 0);
          acc.orders += Number(r.orders_count || 0);
          return acc;
        }, { revenue: 0, orders: 0, items: 0 });
        setShopifySummary(totals);
        try {
          const mapped = rows.map(r => ({
            day: r.day,
            orders_count: r.orders_count,
            revenue: Number(r.total_value_sum || 0),
            items_count: 0,
          }));
          const sorted = [...mapped].sort((a, b) => new Date(b.day || b.date) - new Date(a.day || a.date));
          setShopifyDailyRows(sorted.slice(0, 10));
        } catch {
          setShopifyDailyRows([]);
        }
      } catch {
        setShopifySummary(null);
        setShopifyDailyRows([]);
      } finally {
        setIsLoading(prev => ({ ...prev, shopify: false }));
      }
    };

    fetchShopifySummary();
  }, [brandId, dateRange, startDate, endDate, hasShopifyConnected, getComparisonWindows]);

  // --- WooCommerce Summary ---
  useEffect(() => {
    if (!hasWooConnected) {
      setWooSummary(null);
      setWooDailyRows([]);
      return;
    }
    if (!brandId) return;
    const windows = getComparisonWindows();
    if (!windows) return;

    const fetchWooSummary = async () => {
      try {
        setIsLoading(prev => ({ ...prev, woo: true }));
        const params = new URLSearchParams({ startDate: windows.current.start, endDate: windows.current.end });
        const res = await fetchWithBrand(`/api/woocommerce/analytics/orders-daily?${params.toString()}`, brandId);
        if (!res.ok) {
          setWooSummary(null);
          setWooDailyRows([]);
          return;
        }
        const js = await res.json().catch(() => ({}));
        const rows = Array.isArray(js?.rows) ? js.rows : [];
        const totals = rows.reduce((acc, r) => {
          acc.revenue += Number(r.revenue || 0);
          acc.orders += Number(r.orders_count || 0);
          acc.items += Number(r.items_count || 0);
          return acc;
        }, { revenue: 0, orders: 0, items: 0 });
        setWooSummary(totals);
        try {
          const mapped = rows.map(r => ({
            day: r.day,
            orders_count: r.orders_count,
            revenue: Number(r.revenue || 0),
            items_count: Number(r.items_count || 0),
          }));
          const sorted = [...mapped].sort((a, b) => new Date(b.day || b.date) - new Date(a.day || a.date));
          setWooDailyRows(sorted.slice(0, 10));
        } catch {
          setWooDailyRows([]);
        }
      } catch {
        setWooSummary(null);
        setWooDailyRows([]);
      } finally {
        setIsLoading(prev => ({ ...prev, woo: false }));
      }
    };

    fetchWooSummary();
  }, [brandId, dateRange, startDate, endDate, hasWooConnected, getComparisonWindows]);

  // --- Adjust Summary ---
  useEffect(() => {
    if (!hasAdjustConnected) {
      setAdjustSummary(null);
      setAdjustDailyRows([]);
      return;
    }
    if (!brandId) return;
    const windows = getComparisonWindows();
    if (!windows) return;

    const fetchAdjustSummary = async () => {
      try {
        setIsLoading(prev => ({ ...prev, adjust: true }));
        const params = new URLSearchParams({ startDate: windows.current.start, endDate: windows.current.end });
        const res = await fetchWithBrand(`/api/adjust/performance/daily?${params.toString()}`, brandId);
        if (!res.ok) {
          setAdjustSummary(null);
          setAdjustDailyRows([]);
          return;
        }
        const js = await res.json().catch(() => ({}));
        const rows = Array.isArray(js?.rows) ? js.rows : [];
        const totals = rows.reduce((acc, r) => {
          acc.revenue += Number(r.all_revenue || r.revenue || 0);
          acc.installs += Number(r.installs || 0);
          acc.purchases += Number(r.purchases || 0);
          acc.sessions += Number(r.sessions || 0);
          return acc;
        }, { revenue: 0, installs: 0, purchases: 0, sessions: 0 });
        setAdjustSummary(totals);
        try {
          const sorted = [...rows].sort((a, b) => new Date(b.day || b.date) - new Date(a.day || a.date));
          setAdjustDailyRows(sorted.slice(0, 10));
        } catch {
          setAdjustDailyRows(rows.slice(-10).reverse());
        }
      } catch {
        setAdjustSummary(null);
        setAdjustDailyRows([]);
      } finally {
        setIsLoading(prev => ({ ...prev, adjust: false }));
      }
    };

    fetchAdjustSummary();
  }, [brandId, dateRange, startDate, endDate, hasAdjustConnected, getComparisonWindows]);

  // --- Product Analytics (top10, category, daily) ---
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    if (!brandId) return;

    const fetchEcommerceData = async () => {
      setIsLoading(prev => ({ ...prev, products: true, categories: true, daily: true }));

      // Demo data
      if (isDemoBrand(brandId)) {
        try {
          const demoTop = [
            { itemName: 'Klasik Deri Sneaker - Beyaz', itemBrand: 'Premium Collection', totalRevenue: 285000, totalItemsPurchased: 152 },
            { itemName: 'Kosu Ayakkabisi Pro Max', itemBrand: 'Sport Line', totalRevenue: 248000, totalItemsPurchased: 98 },
            { itemName: 'Yuruyus Botu Outdoor', itemBrand: 'Adventure Gear', totalRevenue: 195000, totalItemsPurchased: 65 },
            { itemName: 'Platform Sneaker Kadin', itemBrand: 'Trend Fashion', totalRevenue: 178000, totalItemsPurchased: 89 },
            { itemName: 'Deri Bot Kislik Siyah', itemBrand: 'Winter Collection', totalRevenue: 156000, totalItemsPurchased: 52 },
            { itemName: 'Basketbol Ayakkabisi High Top', itemBrand: 'Sport Line', totalRevenue: 142000, totalItemsPurchased: 48 },
            { itemName: 'Casual Oxford Erkek', itemBrand: 'Classic Wear', totalRevenue: 128000, totalItemsPurchased: 82 },
            { itemName: 'Kadin Spor Ayakkabi Pembe', itemBrand: 'Active Style', totalRevenue: 115000, totalItemsPurchased: 64 },
            { itemName: 'Trekking Ayakkabisi Pro', itemBrand: 'Adventure Gear', totalRevenue: 98000, totalItemsPurchased: 32 },
            { itemName: 'Slip-On Loafer Kahverengi', itemBrand: 'Comfort Plus', totalRevenue: 85000, totalItemsPurchased: 68 },
          ];
          const demoCats = [
            { itemCategory: 'Ayakkabi > Sneaker > Gunluk', totalRevenue: 485000 },
            { itemCategory: 'Ayakkabi > Spor > Kosu', totalRevenue: 362000 },
            { itemCategory: 'Ayakkabi > Bot > Outdoor', totalRevenue: 298000 },
            { itemCategory: 'Ayakkabi > Kadin > Platform', totalRevenue: 245000 },
            { itemCategory: 'Ayakkabi > Klasik > Oxford', totalRevenue: 178000 },
            { itemCategory: 'Ayakkabi > Cocuk > Okul', totalRevenue: 125000 },
          ];
          const today = new Date();
          const dailyData = [];
          for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const variance = 0.7 + Math.random() * 0.6;
            dailyData.push({
              date: date.toISOString().split('T')[0],
              revenue: Math.round(52000 * variance),
              transactions: Math.round(28 * variance),
              sessions: Math.round(4500 * variance),
              users: Math.round(3200 * variance),
            });
          }
          const totals = {
            revenue: dailyData.reduce((s, d) => s + d.revenue, 0),
            transactions: dailyData.reduce((s, d) => s + d.transactions, 0),
            sessions: dailyData.reduce((s, d) => s + d.sessions, 0),
            users: dailyData.reduce((s, d) => s + d.users, 0),
          };
          const demoDaily = { data: dailyData, totals, changes: { revenue: 12.5, transactions: 8.3, sessions: 15.2, users: 11.8 } };
          setTopProducts(demoTop);
          setCategoryPerformance(demoCats);
          setDailyProductData(demoDaily);
        } finally {
          setIsLoading(prev => ({ ...prev, products: false, categories: false, daily: false }));
        }
        return;
      }

      try {
        let topProductsUrl = `/api/google-ads/top10-daily-performance?dateRange=${dateRange}`;
        let categoryUrl = `/api/google-ads/monthly-category-summary?dateRange=${dateRange}`;
        let dailyPerformanceUrl = `/api/google-ads/daily-product-performance?dateRange=${dateRange}`;
        if (dateRange === 'custom' && startDate && endDate) {
          const startStr = formatDateForAPI(startDate);
          const endStr = formatDateForAPI(endDate);
          topProductsUrl = `/api/google-ads/top10-daily-performance?dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
          categoryUrl = `/api/google-ads/monthly-category-summary?dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
          dailyPerformanceUrl = `/api/google-ads/daily-product-performance?dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
        }

        const topProductsPromise = fetchWithBrand(topProductsUrl, brandId).then(res => res.ok ? res.json() : []);
        const categoryPromise = fetchWithBrand(categoryUrl, brandId).then(res => res.ok ? res.json() : { data: [], totals: {} });
        const dailyPerformancePromise = fetchWithBrand(dailyPerformanceUrl, brandId).then(res => res.ok ? res.json() : { data: [], totals: {}, changes: {} });

        const [topProductsData, categoryData, dailyData] = await Promise.all([topProductsPromise, categoryPromise, dailyPerformancePromise]);
        setTopProducts(topProductsData);
        setCategoryPerformance(categoryData.data || []);
        setDailyProductData(dailyData);
      } catch {
        // silent
      } finally {
        setIsLoading(prev => ({ ...prev, products: false, categories: false, daily: false }));
      }
    };

    fetchEcommerceData();
  }, [brandId, dateRange, startDate, endDate]);

  return {
    hasAdjustConnected,
    ikasSummary,
    ikasDailyRows,
    shopifySummary,
    shopifyDailyRows,
    wooSummary,
    wooDailyRows,
    adjustSummary,
    adjustDailyRows,
    topProducts,
    categoryPerformance,
    dailyProductData,
    isLoading,
  };
}
